// src/testMode/testMode.js
// Test mode lets you step through a tournament quickly (see TestPanel). It is switched on by
//   - REACT_APP_TEST_MODE=true at build/start time, or
//   - opening the app with ?testMode=1 (sticks across reloads; ?testMode=0 switches it off).
import { useEffect, useRef } from 'react';

const STORAGE_KEY = 'tournamental.testMode';

export const TEST_USER = { uid: 'test-user', displayName: 'Test User' };
export const DATA_CHANGED_EVENT = 'tournamental:data-changed';

function readStorage(key) {
    try {
        return window.localStorage.getItem(key);
    } catch (error) {
        return null;
    }
}

function writeStorage(key, value) {
    try {
        if (value === null) {
            window.localStorage.removeItem(key);
        } else {
            window.localStorage.setItem(key, value);
        }
    } catch (error) {
        // Storage can be unavailable (private mode); test mode then just doesn't persist.
    }
}

export const isTestModeForced = () => process.env.REACT_APP_TEST_MODE === 'true';

// The URL switch would let any visitor of a deployed site use the test-user bypass, so it only
// exists outside production builds. (Forcing it with REACT_APP_TEST_MODE still works everywhere.)
const urlSwitchAllowed = () => process.env.NODE_ENV !== 'production';

export const isTestMode = () =>
    isTestModeForced() || (urlSwitchAllowed() && readStorage(STORAGE_KEY) === '1');

export function setTestMode(enabled) {
    writeStorage(STORAGE_KEY, enabled ? '1' : null);
}

// ?testMode=1 / ?testMode=0 in the URL switches the mode on / off.
if (urlSwitchAllowed()) {
    const urlSetting = new URLSearchParams(window.location.search).get('testMode');
    if (urlSetting === '1') setTestMode(true);
    if (urlSetting === '0') setTestMode(false);
}

// The panel changes data behind the pages' back; pages listen for this and refetch.
export const notifyDataChanged = () => window.dispatchEvent(new Event(DATA_CHANGED_EVENT));

export function useDataChanged(callback) {
    const callbackRef = useRef(callback);
    useEffect(() => {
        callbackRef.current = callback;
    });
    useEffect(() => {
        const handler = () => callbackRef.current();
        window.addEventListener(DATA_CHANGED_EVENT, handler);
        return () => window.removeEventListener(DATA_CHANGED_EVENT, handler);
    }, []);
}
